import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useSKU } from 'hooks/useSKU';
import { useAppSelector } from 'store/hooks';
import { washhubSelector } from 'store/slices/washhub';
import { createQALog, fetchQALog } from 'api/QALog';
import { createReadyPackages } from 'api/packages';
import { useMutation, useQueryClient } from 'react-query';
import { errorToast } from 'utils/errorToast';
import { successToast } from 'utils/successToast';
import { FetchResponse } from 'types/common';
import { QALog } from 'types/QALog';
import { RefType, AddQALogModalProps, QAItem } from './AddQALogModal.props';
import AddQALogModalView from './AddQALogModal.view';
import { washHub } from 'store/slices';
import { toApiPayloadDateFormat } from 'utils/dateUtils';
import {
  buildReadyPackagesPayload,
  getCreatedQaLogId,
  QA_LOG_LEFTOVERS_QUERY_KEY,
  sumLeftoverQuantitiesBySku,
} from './AddQALogModal.utils';

const Container = forwardRef<RefType, AddQALogModalProps>((_, ref) => {
  const queryClient = useQueryClient();
  const washHubs = useAppSelector(washhubSelector);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const [selectedSkus, setSelectedSkus] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data: skus = [], isLoading: isLoadingSKU } = useSKU(
    'item',
    washHubId,
  );

  const { mutate, isLoading } = useMutation(
    async ({
      payload,
      addToPackages,
      combineLeftovers,
      items,
    }: {
      payload: Parameters<typeof createQALog>[0];
      addToPackages: boolean;
      combineLeftovers: boolean;
      items: QAItem[];
    }) => {
      const created = await createQALog(payload);

      if (!addToPackages) {
        return;
      }

      const leftoverResponse =
        queryClient.getQueryData<FetchResponse<QALog>>([
          QA_LOG_LEFTOVERS_QUERY_KEY,
          washHubId,
        ]) ||
        (await queryClient.fetchQuery(
          [QA_LOG_LEFTOVERS_QUERY_KEY, washHubId],
          () =>
            fetchQALog({
              washHub: washHubId,
              page: 1,
              limit: 500,
            }),
        ));
      const leftoverBySku = sumLeftoverQuantitiesBySku(
        leftoverResponse?.items || [],
      );
      const packagesPayload = buildReadyPackagesPayload(washHubId, items, {
        qaLog: getCreatedQaLogId(created),
        leftoverBySku,
        combineLeftovers,
      });

      if (packagesPayload.items.length === 0) {
        return;
      }

      try {
        await createReadyPackages(packagesPayload);
      } catch (error: any) {
        errorToast(
          error?.response?.data?.message ||
            error?.message ||
            'QA log created, but adding packages failed',
        );
      }
    },
    {
      onSuccess: () => {
        successToast('QA Log created successfully');

        queryClient.invalidateQueries({
          queryKey: ['fetch-qa-log'],
        });
        queryClient.invalidateQueries({
          queryKey: [QA_LOG_LEFTOVERS_QUERY_KEY],
        });

        onClose();
        setSelectedSkus([]);
      },
      onError: (error: any) => {
        errorToast(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to create QA log',
        );
      },
    },
  );

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [onOpen],
  );

  const onSubmit = (values: any) => {
    const {
      employee,
      date,
      hours,
      items,
      notes,
      addToPackages,
      combineLeftovers,
    } = values || {};
    const updatedData = items.map((item: QAItem) => ({
      sku: item.sku,
      ...(item.passed !== undefined ? { passed: Number(item.passed) } : {}),
      ...(item.dirty !== undefined ? { dirty: Number(item.dirty) } : {}),
      ...(item.damaged !== undefined ? { damaged: Number(item.damaged) } : {}),
      ...(item.total !== undefined ? { total: Number(item.total) } : {}),
      ...(item.yield !== undefined ? { yield: Number(item.yield) } : {}),
    }));

    const QAlogDate = toApiPayloadDateFormat(date);

    const payload = {
      employee: employee,
      date: QAlogDate,
      hours: Number(hours),
      items: updatedData,
      notes: notes || undefined,
      washHub: washHubId,
    };

    mutate({
      payload,
      addToPackages: Boolean(addToPackages),
      combineLeftovers: Boolean(combineLeftovers),
      items,
    });
  };

  const _props = {
    skus,
    isOpen,
    onClose,
    onSubmit,
    washHubs,
    isLoading,
    isLoadingSKU,
    selectedSkus,
    setSelectedSkus,
    washHubId,
  };

  return <AddQALogModalView {..._props} />;
});

const AddQALogModal = memo(Container);

export default AddQALogModal;
